import React, { useContext, useEffect, useState } from 'react'
import { AppSettings } from '../../../config/app-settings'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Loader from '../../pages/loader'
import ReusableTable from '../../../components/other/ReuseableTable'
import { Button } from 'antd'
import {
  useGetSearchEngineLinksQuery,
  useMarkingSearchEngineMutation
} from '../../../redux/apis/linkManagement/searchEngine/lMSearchEngineApiSlice'
import { useGetCampaignByIdQuery } from '../../../redux/apis/campaignsManagement/campaigns/cMCampaignsApiSlice'

const SearchEngineCampaignReview = () => {
  const context = useContext(AppSettings)
  const { id } = useParams()

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  })

  const [selectedStatuses, setSelectedStatuses] = useState({})
  const [markSearchEngine, { isLoading: markLoading }] = useMarkingSearchEngineMutation()

  const { data, isLoading, error } = useGetSearchEngineLinksQuery({
    expand: ['inserted_by'].join(','),
    page: pagination.current,
    page_size: pagination.pageSize,
    fields: ['inserted_by', 'url', 'id'].join(','),
    campaigns: id,
    reviewed:false
  })

  const { data: campaign } = useGetCampaignByIdQuery({ id, fields: 'campaign_name' })

  useEffect(() => {
    if (error) toast.error('Error fetching links')
  }, [error])

  useEffect(() => {
    if (data?.data?.count === 0) {
      toast.info('All links processed. Closing tab...')
      const timeout = setTimeout(() => {
        window.close()
      }, 1500)

      return () => clearTimeout(timeout)
    }
  }, [data?.data?.count])

  const handleTableChange = paginationInfo => {
    setPagination({
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    })
  }

  const handleBulkSelect = (status) => {
    const updated = {}
    data?.data?.results?.forEach(row => {
      updated[row.id] = status
    })
    setSelectedStatuses(updated)
  }

  const handleSubmit = async () => {
    const payload = Object.entries(selectedStatuses).map(([id, status]) => {
      const record = data?.data?.results?.find(item => item.id.toString() === id)
      return {
        url: record?.url,
        status
      }
    })

    try {
      const response = await markSearchEngine(payload).unwrap()
      if (response?.success) {
        toast.success('Links updated successfully')
        setSelectedStatuses({})
      } else {
        response?.errors?.forEach(err =>
          toast.error(`${err.url}: ${err.error}`)
        )
      }
    } catch (error) {
      toast.error('Failed to update links.')
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', sorter: true, search: true },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      width: 500,
      ellipsis: true,
      render: text => (
        <a href={text} target='_blank' rel='noreferrer'>
          <p>{decodeURIComponent(text)}</p>
        </a>
      )
    },
    {
      title: 'Inserted BY',
      dataIndex: 'inserted_by',
      key: 'inserted_by',
      render: text => (
        <p>
          {text?.first_name} {text?.last_name}
        </p>
      )
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => {
        const currentStatus = selectedStatuses[record.id] || ''
        return (
          <div className='flex gap-1 flex-col'>
            {['related', 'unrelated'].map(status => (
              <label key={status} className='flex gap-2'>
                <input
                  type='radio'
                  name={`status-${record.id}`}
                  value={status}
                  checked={currentStatus === status}
                  onChange={() =>
                    setSelectedStatuses(prev => ({
                      ...prev,
                      [record.id]: status
                    }))
                  }
                />
                <span>{status}</span>
              </label>
            ))}
          </div>
        )
      }
    }
  ]

  useEffect(() => {
    context.setAppHeaderNone(false)
    context.setAppSidebarNone(false)
    context.setAppContentClass('')
    context.setAppThemePanal(true)

    return () => {
      context.setAppHeaderNone(true)
      context.setAppSidebarNone(true)
      context.setAppContentClass('p-0')
      context.setAppThemePanal(false)
    }
  }, [context])

  return (
    <div className='w-full flex flex-col'>
      <Loader isLoading={isLoading || markLoading} />

      <h4 className='mb-3'>{campaign?.data?.campaign_name}</h4>

      <div className='row'>
        <ReusableTable
          columns={columns}
          data={data?.data?.results}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.data?.count,
            showSizeChanger: true,
          }}
          onChange={handleTableChange}
        />
      </div>

      <div className='flex gap-4 mt-4'>
        {['related', 'unrelated'].map(status => (
          <Button
            key={status}
            type='default'
            onClick={() => handleBulkSelect(status)}
          >
            Select All as {status}
          </Button>
        ))}

        <button
          className='btn btn-theme'
          onClick={handleSubmit}
          disabled={Object.keys(selectedStatuses).length === 0}
        >
          Submit
        </button>
      </div>
    </div>
  )
}

export default SearchEngineCampaignReview
